/**
 * API Response Transformer
 *
 * Transforms API responses for enhanced display.
 * Use these transformers when receiving data from the API.
 *
 * @example
 * const transaction = ApiTransformer.transformTransaction(apiResponse);
 * console.log(transaction.actualAmountFormatted); // "$10.50"
 */

import { Money } from './money';
import { DateTime } from './dateTime';

/**
 * Transforms object keys from camelCase to snake_case
 * Used for sending data to the backend API
 */
export function transformToApi(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(transformToApi);
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const transformed: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Convert camelCase to snake_case
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      transformed[snakeKey] = transformToApi(value);
    }
    return transformed;
  }

  return obj;
}

/**
 * Transforms object keys from snake_case to camelCase
 * Used for receiving data from the backend API
 */
export function transformFromApi(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(transformFromApi);
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const transformed: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Preserve MongoDB's _id field as-is; convert all other snake_case keys to camelCase
      const camelKey = key === '_id'
        ? '_id'
        : key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      transformed[camelKey] = transformFromApi(value);
    }
    return transformed;
  }

  return obj;
}

export class ApiTransformer {
  /**
   * Transform transaction response
   * Adds formatted amounts for display
   */
  static transformTransaction(transaction: any) {
    if (!transaction) return null;

    return {
      ...transaction,
      // Add formatted versions for display
      actualAmountFormatted: Money.formatCents(transaction.actualAmountCents || 0),
      paidAmountFormatted: Money.formatCents(transaction.paidAmountCents || 0),
      
      // Format timestamps
      createdAtFormatted: transaction.created_at
        ? DateTime.formatTimestamp(transaction.created_at, 'long')
        : 'N/A',
      createdAtRelative: transaction.created_at
        ? DateTime.formatTimestamp(transaction.created_at, 'relative')
        : 'N/A',
    };
  }

  /**
   * Transform coupon response
   * Formats dates and discount information
   */
  static transformCoupon(coupon: any) {
    if (!coupon) return null;

    return {
      ...coupon,
      // Format timestamps for display
      validFromFormatted: coupon.valid_from
        ? DateTime.formatTimestamp(coupon.valid_from, 'short')
        : 'N/A',
      validUntilFormatted: coupon.valid_until
        ? DateTime.formatTimestamp(coupon.valid_until, 'short')
        : 'N/A',
      
      // Add display string for discount type
      discountTypeDisplay: coupon.discount_type === 'percentage' 
        ? 'Percentage' 
        : 'Fixed Amount',
      
      // Format discount value
      discountValueFormatted: coupon.discount_type === 'percentage'
        ? `${coupon.discount_value}%`
        : Money.formatCents(coupon.discount_value || 0),
      
      // Check if coupon is currently valid
      isCurrentlyValid: coupon.valid_from && coupon.valid_until
        ? DateTime.now() >= coupon.valid_from && DateTime.now() <= coupon.valid_until
        : false,
      
      // Check if coupon is expired
      isExpired: coupon.valid_until
        ? DateTime.isPast(coupon.valid_until)
        : false,
    };
  }

  /**
   * Transform user/owner response
   * Formats timestamps and adds display flags
   */
  static transformUser(user: any) {
    if (!user) return null;

    return {
      ...user,
      // Format timestamps
      createdAtFormatted: user.created_at
        ? DateTime.formatTimestamp(user.created_at, 'long')
        : 'N/A',
      updatedAtFormatted: user.updated_at
        ? DateTime.formatTimestamp(user.updated_at, 'long')
        : 'N/A',
      
      // Add display flags
      hasMFA: user.mfa_enabled === true,
      isActive: user.is_active !== false,
    };
  }

  /**
   * Transform key response
   * Formats subscription information
   */
  static transformKey(key: any) {
    if (!key) return null;

    return {
      ...key,
      // Format subscription expiry
      subscriptionExpiresAtFormatted: key.subscription_expires_at
        ? DateTime.formatTimestamp(key.subscription_expires_at, 'short')
        : 'N/A',
      subscriptionExpiresAtRelative: key.subscription_expires_at
        ? DateTime.formatTimestamp(key.subscription_expires_at, 'relative')
        : 'N/A',
      
      // Check subscription status
      isSubscriptionExpired: key.subscription_expires_at
        ? DateTime.isPast(key.subscription_expires_at)
        : false,
      isSubscriptionActive: key.subscription_expires_at
        ? DateTime.isFuture(key.subscription_expires_at)
        : false,
      
      // Format timestamps
      createdAtFormatted: key.created_at
        ? DateTime.formatTimestamp(key.created_at, 'long')
        : 'N/A',
    };
  }

  /**
   * Transform box response
   * Adds formatted location and capacity information
   */
  static transformBox(box: any) {
    if (!box) return null;

    return {
      ...box,
      // Format location
      locationFormatted: box.location?.coordinates
        ? `${box.location.coordinates[1].toFixed(6)}, ${box.location.coordinates[0].toFixed(6)}`
        : 'N/A',
      
      // Calculate capacity percentage
      capacityPercentage: box.total_slots > 0
        ? Math.round((box.occupied_slots / box.total_slots) * 100)
        : 0,
      
      // Add display flags
      isFull: box.available_slots === 0,
      isEmpty: box.occupied_slots === 0,
      hasAvailableSlots: box.available_slots > 0,
      
      // Format timestamps
      createdAtFormatted: box.created_at
        ? DateTime.formatTimestamp(box.created_at, 'long')
        : 'N/A',
      lastMaintenanceFormatted: box.last_maintenance_at
        ? DateTime.formatTimestamp(box.last_maintenance_at, 'short')
        : 'Never',
    };
  }

  /**
   * Transform subscription plan response
   * Formats pricing information
   */
  static transformSubscriptionPlan(plan: any) {
    if (!plan) return null;

    return {
      ...plan,
      // Format price
      priceFormatted: plan.price_cents
        ? Money.formatCents(plan.price_cents)
        : 'N/A',
      
      // Format interval
      intervalDisplay: plan.interval === 'month'
        ? 'Monthly'
        : plan.interval === 'year'
        ? 'Yearly'
        : plan.interval || 'Unknown',
    };
  }

  /**
   * Transform array of items
   * Applies the appropriate transformer to each item
   */
  static transformArray<T>(items: any[], transformer: (item: any) => T): T[] {
    if (!Array.isArray(items)) return [];
    return items.map(item => transformer(item));
  }

  /**
   * Transform paginated response
   * Transforms the data array and preserves pagination info
   */
  static transformPaginated<T>(
    response: any,
    transformer: (item: any) => T
  ): {
    data: T[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  } {
    return {
      data: this.transformArray(response.data || [], transformer),
      pagination: {
        page: response.page || 1,
        limit: response.limit || 10,
        total: response.total || 0,
        totalPages: response.totalPages || 0,
      },
    };
  }
}

export default ApiTransformer;